// AudioRecorder hook — continuous, hands-free voice dictation.
//
// Click the button once to start listening; the hook records with MediaRecorder,
// watches the live microphone level (Web Audio AnalyserNode), and — when it
// detects a natural pause (silence) after speech — finalizes that utterance and
// pushes it to the server for transcription (`seiva_audio_to_text`), then keeps
// listening for the next utterance. The transcript is appended to the composer
// so the user can review/edit before sending. Click again (or a long silence)
// stops. One model call per utterance keeps cost bounded (no streaming ASR).
//
// The same AnalyserNode drives the level-reactive equalizer animation via the
// `--mic-level` CSS custom property.
//
// Contract with the server LiveView:
//   - pushes `transcribe_audio` { audio_base64, mime_type } per finalized utterance.
//     Client serializes: waits for `transcribe_done`/`transcribe_error` before
//     sending the next, so transcripts append in order.
//   - pushes `audio_recorder_error` { kind } on capture failure
//     (kind ∈ "permission" | "no_device" | "insecure" | "capture" | "too_large").
//
// Graceful degradation: if Web Audio is unavailable, falls back to manual mode
// (click to start, click to stop → one transcription).

const MAX_BYTES = 8 * 1024 * 1024 // ~8 MB per utterance, below the 10 MB JSON cap

// VAD tuning (milliseconds / normalized RMS level 0..1).
const SPEECH_LEVEL = 0.02 // above this = speech
const SILENCE_MS = 1200 // pause after speech → finalize the utterance
const MIN_SPEECH_MS = 350 // ignore blips shorter than this (coughs, clicks)
const MAX_SEGMENT_MS = 15000 // hard cap on a single utterance
const IDLE_STOP_MS = 12000 // no speech at all for this long → stop entirely

// Ordered by preference; first supported wins. Safari only supports mp4.
const MIME_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/ogg",
]

export default {
  mounted() {
    this.listening = false
    this.stream = null
    this.audioCtx = null
    this.analyser = null
    this.levelData = null
    this.rafId = null
    this.mimeType = "audio/webm"

    this.recorder = null
    this.segStartTs = 0
    this.segHadSpeech = false
    this.segFirstSpeechTs = 0
    this.segLastSpeechTs = 0

    this.queue = []
    this.sending = false

    this._onClick = () => this._toggle()
    this.el.addEventListener("click", this._onClick)

    this.handleEvent("transcribe_done", () => {
      this.sending = false
      this._drainQueue()
    })
    this.handleEvent("transcribe_error", () => {
      this.sending = false
      this._drainQueue()
    })
  },

  updated() {
    // Self-heal the recording class after a server DOM patch (morphdom may reset
    // the class attribute the server owns; re-apply what we own).
    this.el.classList.toggle("is-recording", this.listening)
  },

  destroyed() {
    this._teardown()
  },

  _toggle() {
    if (this.el.disabled) return
    if (this.listening) {
      this._stopListening()
    } else {
      this._startListening()
    }
  },

  async _startListening() {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      this._pushServer("audio_recorder_error", { kind: "insecure" })
      return
    }

    let stream
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch (err) {
      const name = err && err.name
      let kind = "capture"
      if (name === "NotAllowedError" || name === "SecurityError") kind = "permission"
      else if (name === "NotFoundError" || name === "DevicesNotFoundError") kind = "no_device"
      this._pushServer("audio_recorder_error", { kind })
      return
    }

    this.stream = stream
    this.mimeType = this._pickMime()
    this.listening = true
    this.el.classList.add("is-recording")
    this.el.setAttribute("aria-pressed", "true")

    this._setupAnalyser(stream)
    this._startSegment()

    if (this.analyser) {
      this._tick()
    }
    // Without an analyser we run in manual mode: recording continues until the
    // user clicks stop (no VAD auto-segmentation).
  },

  _stopListening() {
    this.listening = false
    this.el.classList.remove("is-recording")
    this.el.setAttribute("aria-pressed", "false")
    this.el.style.setProperty("--mic-level", "0")

    if (this.rafId) {
      cancelAnimationFrame(this.rafId)
      this.rafId = null
    }
    // Finalize the last utterance (transcribe it) but don't start a new segment.
    this._finalizeSegment(false)
    this._teardownAudio()
  },

  // ── Audio level + VAD ─────────────────────────────────────────────────────

  _setupAnalyser(stream) {
    const Ctx = window.AudioContext || window.webkitAudioContext
    if (!Ctx) {
      this.analyser = null
      return
    }
    try {
      this.audioCtx = new Ctx()
      if (this.audioCtx.state === "suspended") this.audioCtx.resume()
      const source = this.audioCtx.createMediaStreamSource(stream)
      this.analyser = this.audioCtx.createAnalyser()
      this.analyser.fftSize = 512
      this.levelData = new Uint8Array(this.analyser.fftSize)
      source.connect(this.analyser)
    } catch (_err) {
      this.analyser = null
    }
  },

  _tick() {
    if (!this.listening || !this.analyser) return

    const level = this._readLevel()
    this.el.style.setProperty("--mic-level", level.toFixed(3))

    const now = Date.now()
    if (level >= SPEECH_LEVEL) {
      if (!this.segHadSpeech) this.segFirstSpeechTs = now
      this.segHadSpeech = true
      this.segLastSpeechTs = now
    }

    const segAge = now - this.segStartTs

    if (this.segHadSpeech && now - this.segLastSpeechTs >= SILENCE_MS) {
      // Natural pause → finalize this utterance and keep listening.
      this._finalizeSegment(true)
    } else if (this.segHadSpeech && segAge >= MAX_SEGMENT_MS) {
      this._finalizeSegment(true)
    } else if (!this.segHadSpeech && segAge >= IDLE_STOP_MS) {
      // Nothing said for a while → release the mic.
      this._stopListening()
      return
    }

    this.rafId = requestAnimationFrame(() => this._tick())
  },

  _readLevel() {
    this.analyser.getByteTimeDomainData(this.levelData)
    let sum = 0
    for (let i = 0; i < this.levelData.length; i++) {
      const v = (this.levelData[i] - 128) / 128 // → [-1, 1]
      sum += v * v
    }
    return Math.min(1, Math.sqrt(sum / this.levelData.length))
  },

  // ── Segment (utterance) recording ─────────────────────────────────────────

  _startSegment() {
    if (!this.stream) return
    let recorder
    try {
      recorder = this.mimeType
        ? new MediaRecorder(this.stream, { mimeType: this.mimeType })
        : new MediaRecorder(this.stream)
    } catch (_err) {
      this._stopListening()
      this._pushServer("audio_recorder_error", { kind: "capture" })
      return
    }

    recorder._chunks = []
    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) recorder._chunks.push(e.data)
    }
    recorder.onstop = () => this._onSegmentStop(recorder)

    this.recorder = recorder
    this.segStartTs = Date.now()
    this.segHadSpeech = false
    this.segFirstSpeechTs = 0
    this.segLastSpeechTs = 0
    recorder.start()
  },

  _finalizeSegment(continueListening) {
    const recorder = this.recorder
    // Snapshot whether this segment is worth transcribing before we reset state.
    if (recorder) {
      recorder._worthSending =
        this.segHadSpeech && this.segLastSpeechTs - this.segFirstSpeechTs >= MIN_SPEECH_MS
      recorder._mime = (recorder.mimeType || this.mimeType || "audio/webm").split(";")[0]
    }
    this.recorder = null

    if (recorder && recorder.state !== "inactive") {
      recorder.stop() // onstop → _onSegmentStop enqueues if worthSending
    }

    if (continueListening && this.listening) {
      this._startSegment()
    }
  },

  _onSegmentStop(recorder) {
    const blob = new Blob(recorder._chunks || [], { type: recorder._mime || "audio/webm" })
    recorder._chunks = []

    if (!recorder._worthSending || !blob.size) return
    if (blob.size > MAX_BYTES) {
      this._pushServer("audio_recorder_error", { kind: "too_large" })
      return
    }

    const reader = new FileReader()
    reader.onloadend = () => {
      const result = reader.result || ""
      const comma = result.indexOf(",")
      const base64 = comma >= 0 ? result.slice(comma + 1) : ""
      if (base64) {
        this.queue.push({ base64, mime: recorder._mime || "audio/webm" })
        this._drainQueue()
      }
    }
    reader.readAsDataURL(blob)
  },

  // ── Serialized send queue (keeps transcript order) ────────────────────────

  _drainQueue() {
    if (this.sending || this.queue.length === 0) return
    const item = this.queue.shift()
    this.sending = true
    this._pushServer("transcribe_audio", {
      audio_base64: item.base64,
      mime_type: item.mime,
    })
  },

  // The Diagnostic Room owns its recorder in the root LiveView, while Matt's
  // recorder lives in a LiveComponent. Route to the component when the hook
  // element carries `phx-target`; keep the original LiveView behavior for all
  // existing recorder surfaces.
  _pushServer(event, payload) {
    if (this.el.hasAttribute("phx-target")) {
      this.pushEventTo(this.el, event, payload)
    } else {
      this.pushEvent(event, payload)
    }
  },

  _pickMime() {
    if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported) return ""
    for (const candidate of MIME_CANDIDATES) {
      if (MediaRecorder.isTypeSupported(candidate)) return candidate
    }
    return ""
  },

  _teardownAudio() {
    if (this.audioCtx) {
      try {
        this.audioCtx.close()
      } catch (_err) {
        // ignore
      }
      this.audioCtx = null
    }
    this.analyser = null
    this.levelData = null
    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop())
      this.stream = null
    }
  },

  _teardown() {
    this.listening = false
    if (this.rafId) {
      cancelAnimationFrame(this.rafId)
      this.rafId = null
    }
    if (this.recorder && this.recorder.state !== "inactive") {
      try {
        this.recorder.stop()
      } catch (_err) {
        // ignore
      }
    }
    this.recorder = null
    this._teardownAudio()
  },
}
